<template>
  <div class="api-user-list">
    <Loading
      class="vasion-loading-indicator"
      :active.sync="isLoading"
      :is-full-page="false"
      :color="loaderColor"
      loader="dots"
      :background-color="loaderBackgroundColor"
    />
    <div v-if="!hasApiUsers && !isLoading" class="no-registries">
      <VasionAccountCircleIcon class="icon" />
      <p class="no-registries-title">No API Users</p>
      <VasionButton classProp="primary" @vasionButtonClicked="newAPIUserClick">
        New API User
      </VasionButton>
    </div>
    <div v-if="hasApiUsers && !isLoading" class="w-100">
      <p class="no-registries-title">Click here to see our <router-link to="/docs/apis">API Documentation</router-link>
      </p>
      <div class="h-100 vasion-html-table-default y-scrollable">
        <VasionTable
          id="api-user-list-table"
          :ellipsisOffset="-180"
          :headerColumns="tableData.Columns.Values"
          :tableRows="tableData.Rows.Values"
          :hideColumns="hiddenColumns"
          :ellipsisButtonConfig="ellipsisButtonConfig"
          @ellipsis-button-clicked="ellipsisButtonClicked"
          @vasion-selection="rowClickHandler"
        />
      </div>
    </div>
    <VasionGeneralModal
      id="tokens-list-delete-prevention"
      modalType="slot"
      :sync="showDeletePreventionModal"
      :hideButtons="true"
    >
      <VasionConfirmationDialog
        message="Are you sure you want to delete this API User?"
        @noButtonClick="showDeletePreventionModal = false"
        @yesButtonClick="deleteApiUser()"
      />
    </VasionGeneralModal>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import { loaderBackgroundColor, loaderColor } from '@/assets/js/styleConfig'

export default {
  name: 'APIUserList',
  components: {
    Loading,
  },
  props: {},
  data: function () {
    return {
      apiUsers: [],
      columns: {
        Values: [
          '_UserID_',
          'Username',
          'Email',
          'Active',
          'Admin',
          '_VasionEllipsisButton_',
        ],
        ValueType: 'String',
      },
      ellipsisButtonConfig: {
        buttons: [
          {
            id: 'edit-user-button',
            name: 'Edit User',
            icon: 'VasionEditIcon',
            permissionGetter: 'common/canConfigureUsers',
          },
          {
            id: 'assign-roles-button',
            name: 'Assign Roles',
            icon: 'VasionSettingsIcon',
            permissionGetter: 'common/canConfigurSecurityRoles',
          },
          {
            id: 'api-user-tokens-button',
            name: 'Manage Tokens',
            icon: 'VasionWebhookIcon',
            permissionGetter: 'common/canConfigureUsers',
          },
          {
            id: 'delete-user-button',
            name: 'Delete User',
            icon: 'VasionDeleteIcon',
            permissionGetter: 'common/canConfigureUsers',
          },
        ],
      },
      hiddenColumns: ['_UserID_', 'Admin'],
      tableData: {},
      showDeletePreventionModal: false,
      userToDelete: {},

      isLoading: false,
      loaderBackgroundColor: loaderBackgroundColor,
      loaderColor: loaderColor,
    }
  },
  async created() {
    await this.loadApiUsersList()
  },
  computed: {
    hasApiUsers() {
      return this.tableData?.Rows?.Values.length > 0
    },
  },
  methods: {
    async buildTableData(dataValues) {
      const dataList = dataValues.dataList || []

      const data = dataList.map(user => ({
        Values: [
          user.value,
          user.username,
          user.email,
          user.active ? 'Yes' : 'No',
          user.admin,
          'VasionEllipsisIcon',
        ],
        ValueType: 'String',
      }))

      return {
        Columns: dataValues.columns,
        Rows: {
          Values: data,
          ValueType: 'ApiArray`1',
        },
      }
    },
    async deleteApiUser() {
      if (!this.userToDelete) return

      const result = await this.$store.dispatch(
        'security/deleteUser',
        this.userToDelete.value
      )

      if (result?.Value === 'True') {
        this.showDeletePreventionModal = false
        await this.loadApiUsersList()
        this.$emit(
          'show-snackbar-with-message',
          'Deleting API User',
          'API User deleted successfully',
          false
        )
      } else {
        this.$emit(
          'show-snackbar-with-message',
          'Error Deleting API User',
          'Failed to delete api user'
        )
      }
    },
    editApiUser(user) {
      const userId = user.value
      this.$emit('edit-api-user', userId)
    },
    async assignRoles(user) {
      this.$emit('assign-roles', user)
    },
    async manageTokens(user) {
      this.$emit('manage-tokens', user)
    },
    ellipsisButtonClicked(payload) {
      const apiUserId = payload?.item?.Values?.[0]
      if (!apiUserId) return

      const user = this.apiUsers.find(u => u.value === apiUserId)
      if (!user) return

      const actions = {
        'edit-user-button': () => this.editApiUser(user),
        'delete-user-button': () => this.preventAPIUserDeletion(user),
        'assign-roles-button': () => this.assignRoles(user),
        'api-user-tokens-button': () => this.manageTokens(user),
      }

      const action = actions[payload.buttonId]
      if (action) action()
    },
    async getAPIUserList() {
      try {
        this.isLoading = true

        const payload = {
          includeDisabled: true,
          onlyExternalAPIUsers: true,
        }
        await this.$store.dispatch('common/getUsers', payload)
        this.apiUsers = await this.$store.state.common.users

        this.isLoading = false
      } catch (error) {
        console.error('Error fetching api user list:', error)
        this.isLoading = false
      }
    },
    async loadApiUsersList() {
      this.isLoading = true

      await this.getAPIUserList()

      const data = {
        dataList: this.apiUsers,
        columns: this.columns,
      }

      this.tableData = await this.buildTableData(data)

      this.isLoading = false
    },
    newAPIUserClick() {
      this.$emit('new-api-user-clicked')
    },
    preventAPIUserDeletion(user) {
      this.userToDelete = user
      this.showDeletePreventionModal = true
    },
    rowClickHandler(data) {
      if (data?.Values[0]) {
        const apiUserId = data.Values[0]
        const user = this.apiUsers.find(u => u.value === apiUserId)
        this.editApiUser(user)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/variables.scss';
@import '@/assets/css/light-theme-system-palette.scss';

.api-user-list {
  display: flex;
  position: relative;
  height: calc(100% - 144px);

  @media (min-width: $breakpoint-md) {
    height: calc(100% - 98px);
  }

  @media (min-width: $breakpoint-md) {
    height: calc(100% - 48px);
  }
}

.api-user-list .vasion-loading-indicator {
  height: 100%;
}

.api-user-list .no-registries {
  height: 100%;
  width: 100%;
  display: flex;
  padding: 24px 0px 80px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.api-user-list .no-registries .icon {
  fill: $outline-variant;
  width: 144px;
  height: 144px;
}

.api-user-list .no-registries .no-registries-title {
  color: $on-surface;
  text-align: center;
  font-family: $font-family-book;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  margin: unset;
}
</style>
